/**
 * "Did you mean …@gmail.com?" for the sign-in email field.
 *
 * Thousands of accounts sit on misspelled domains that never received a
 * single sign-in email (2026-10-12: gmai.com 132, qmail.com 84, gmail.con 65,
 * gmal.com 64, gnail.com 47, ukr.ner 22 …, all with zero sign-ins). The UI asks
 * before sending; the person can always keep what they typed, so a rare false
 * suggestion costs one tap.
 */

// Domains we suggest, most popular first (ties go to the earlier one).
const TARGETS = [
  'gmail.com',
  'ukr.net',
  'i.ua',
  'icloud.com',
  'meta.ua',
  'yahoo.com',
  'bigmir.net',
  'hotmail.com',
  'outlook.com',
]

// Real domains that must never be "corrected", even when close to a target.
const KNOWN = new Set([
  ...TARGETS,
  'googlemail.com', 'me.com', 'mac.com', 'mail.ru', 'rambler.ru', 'ua.fm', 'email.ua', 'online.ua',
  'yahoo.co.uk', 'yahoo.com.ua', 'live.com', 'msn.com', 'aol.com', 'proton.me', 'protonmail.com',
  'gmx.com', 'gmx.de', 'web.de', 'seznam.cz', 'yandex.ru', 'yandex.ua', 'yandex.com', 'bk.ru',
  'inbox.ru', 'list.ru', 'confy.cc', 'ymail.com', 'email.com', 'yahoo.ca',
])

// First label that only ever exists under one real domain: gmail.ru,
// gmail.com.ua, gmail.comcom, ukr.com …
const LABEL_HOME: Record<string, string> = {
  gmail: 'gmail.com',
  ukr: 'ukr.net',
  icloud: 'icloud.com',
  bigmir: 'bigmir.net',
}

/** Optimal string alignment distance (Levenshtein + adjacent swaps). */
function distance(a: string, b: string): number {
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)])
  for (let j = 1; j <= b.length; j++) d[0][j] = j
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost)
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1)
      }
    }
  }
  return d[a.length][b.length]
}

function fixDomain(domain: string): string | null {
  if (KNOWN.has(domain)) return null

  const home = LABEL_HOME[domain.split('.')[0]]
  if (home) return home

  let best: string | null = null
  let bestDistance = Infinity
  for (const target of TARGETS) {
    // Short domains (ukr.net, i.ua) tolerate one slip; longer ones two.
    const allowed = target.length >= 9 ? 2 : 1
    const dist = distance(domain, target)
    if (dist <= allowed && dist < bestDistance) {
      best = target
      bestDistance = dist
    }
  }
  return best
}

/** The corrected address, or null when the domain looks right. */
export function suggestEmailFix(email: string): string | null {
  const trimmed = email.trim()
  const at = trimmed.lastIndexOf('@')
  if (at < 1 || at === trimmed.length - 1) return null
  const local = trimmed.slice(0, at)
  const domain = trimmed.slice(at + 1).toLowerCase()
  const fixed = fixDomain(domain)
  return fixed ? `${local}@${fixed}` : null
}
